import { useSyncExternalStore } from "react";

import { runtimeEnv } from "utils/runtimeEnv";

const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 30_000;

type AnalyticsSdk = {
  get_distinct_id?: () => string | undefined;
  __loaded?: boolean;
};

let ready = false;
let started = false;
let stopPolling: (() => void) | undefined;
let resetGeneration = 0;
const listeners = new Set<() => void>();

function emitReady() {
  ready = true;
  listeners.forEach((listener) => listener());
}

/**
 * posthog persists distinct_id in localStorage and can hand it back before
 * init() finishes, so a resolved id is not a readiness signal there. __loaded is
 * set at the end of init() and is the only trustworthy flag.
 */
function isPosthogReady(sdk: AnalyticsSdk | undefined): boolean {
  return sdk?.__loaded === true;
}

/**
 * mixpanel throws rather than returning an id when called before init(), so a
 * resolved id means init() has run. __loaded is kept as a fallback in case
 * get_distinct_id ever changes shape.
 */
function isMixpanelReady(sdk: AnalyticsSdk | undefined): boolean {
  if (!sdk) return false;
  try {
    if (sdk.get_distinct_id?.()) return true;
  } catch {
    // called before init()
  }
  return sdk.__loaded === true;
}

function startPolling() {
  const generation = resetGeneration;
  const isStale = () => generation !== resetGeneration;

  const needsMixpanel = Boolean(runtimeEnv.VITE_MIXPANEL_TOKEN());
  const needsPosthog = Boolean(runtimeEnv.VITE_POSTHOG_KEY());

  if (!needsMixpanel && !needsPosthog) {
    emitReady();
    return;
  }

  let mixpanel: AnalyticsSdk | undefined;
  let posthog: AnalyticsSdk | undefined;

  const check = () => {
    if (isStale()) return true;
    if (ready) return true;
    if (needsMixpanel && !isMixpanelReady(mixpanel)) return false;
    if (needsPosthog && !isPosthogReady(posthog)) return false;
    emitReady();
    return true;
  };

  const interval = setInterval(() => {
    if (check()) {
      clearInterval(interval);
      clearTimeout(timeout);
    }
  }, POLL_INTERVAL_MS);

  stopPolling = () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };

  const timeout = setTimeout(() => {
    clearInterval(interval);
    if (isStale() || ready) return;
    console.warn(
      `[useAnalyticsReady] SDKs did not report readiness within ${POLL_TIMEOUT_MS}ms. Tracking anyway; early events may be dropped.`
    );
    emitReady();
  }, POLL_TIMEOUT_MS);

  void Promise.all([
    needsMixpanel
      ? import("mixpanel-browser").then((m) => {
          mixpanel = m.default as AnalyticsSdk;
        })
      : undefined,
    needsPosthog
      ? import("posthog-js").then((m) => {
          posthog = m.default as AnalyticsSdk;
        })
      : undefined,
  ]).then(check);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!started && !ready) {
    started = true;
    startPolling();
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * AnalyticsProvider initializes Mixpanel and PostHog behind a dynamic import that
 * only starts once visitor_uuid resolves, and its track() drops events while the
 * SDKs are still loading. Both SDKs resolve to the same module instance the
 * provider imports, so polling them tells us when tracking will actually land.
 *
 * The polling loop is module-level, so every consumer shares one interval and one
 * readiness transition.
 */
/**
 * Test-only. The readiness state above is module-level and shared by every
 * consumer, so without this a single test that reaches ready=true would make all
 * later tests short-circuit and never exercise the polling loop.
 */
export function resetAnalyticsReadyForTests() {
  resetGeneration += 1;
  stopPolling?.();
  stopPolling = undefined;
  ready = false;
  started = false;
  listeners.clear();
}

export function useAnalyticsReady(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => ready,
    () => false
  );
}
